import { __awaiter, __decorate, __param } from "tslib";
import { UUID } from '@genesislcap/foundation-utils';
import { JSONSerializer } from '@genesislcap/foundation-utils';
import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { BehaviorSubject, from, Observable, of, Subject, Subscription, throwError, timer, } from 'rxjs';
import { filter, mergeMap, switchMap, takeWhile, tap } from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { Session } from '../session';
import { logger } from '../utils';
import { EventMessageType, MessageBuilder, MessageType } from './message';
import { exponentialScheduler, linearScheduler, MAX_RECONNECT_ATTEMPTS, retryInterval, SocketReconnectStrategy, } from './reconnectStrategy';
/**
 * Subject that wraps a WebSocketSubject and provides a Subject interface for sending and receiving messages.
 * @public
 * @remarks An alias to RxJS Subject.
 */
export class SocketSubject extends Subject {
    constructor() {
        super();
    }
}
/**
 * Observable that wraps a WebSocketSubject and provides an Observable interface for sending and receiving messages.
 * @public
 * @remarks An alias to RxJS Observable.
 */
export class SocketObservable extends Observable {
    constructor(...args) {
        super(...args);
    }
}
/**
 * An alias to RxJS Subscription.
 * @public
 */
export { Subscription as SocketSubscription };
/**
 * The Socket message handler definition.
 * @public
 */
export class SocketMessageHandler {
    constructor(message, onMessage, onError, isSubscription) {
        this._message = message;
        this._onMessage = onMessage;
        this._onError = onError;
        this._isSubscription = isSubscription;
    }
    /**
     * The message that was sent.
     * @public
     */
    get sentMessage() {
        return this._message;
    }
    /**
     * The handler function for incoming messages.
     * @public
     */
    get onReceivedMessage() {
        return this._onMessage;
    }
    /**
     * The handler function for errors.
     * @public
     */
    get onReceivedError() {
        return this._onError;
    }
    /**
     * Whether the message handler is a subscription.
     * @public
     */
    get isSubscription() {
        return this._isSubscription;
    }
}
let SocketService = class SocketService {
    isConnectedChanged(_, isConnected) {
        this.isConnectedSubject.next(isConnected);
    }
    constructor(messageBuilder, session, serializer, uuid) {
        this.messageBuilder = messageBuilder;
        this.session = session;
        this.serializer = serializer;
        this.uuid = uuid;
        this.defaultInterval = 10000;
        this.secondsMultiplier = 1000;
        this.isConnectedSubject = new BehaviorSubject(false);
        this.isConnected = false;
        this.hasValidSession = false;
        this.heartbeatIsEnabled = true;
        this.heartbeatInterval = this.defaultInterval;
        this.isReconnecting = false;
        this.socketMessages = () => this.wsMessages;
    }
    get heartbeatLoggingIsEnabled() {
        var _a, _b;
        return (_b = (_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.enableHeartbeatLogging) !== null && _b !== void 0 ? _b : false;
    }
    get messageLoggingIsEnabled() {
        var _a, _b;
        return (_b = (_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.enableMessageLogging) !== null && _b !== void 0 ? _b : false;
    }
    connect(host, connectOptions, reconnectOptions) {
        this.url = host;
        this.connectOptions = connectOptions;
        this.reconnectOptions = reconnectOptions;
        return new Promise((resolve) => {
            var _a, _b, _c, _d;
            logger.info(`${this.isReconnecting ? 'Reconnecting' : 'Connecting'} to ${this.url}`);
            const cleanMessages = !this.isReconnecting || !((_a = this.reconnectOptions) === null || _a === void 0 ? void 0 : _a.reconnectStreams);
            if (cleanMessages) {
                this.cleanMessages();
            }
            (_b = this.socketSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
            this.websocket = new WebSocketSubject({
                url: this.url,
                openObserver: {
                    next: () => {
                        this.session.setSessionStorageItem('hostUrl', this.url);
                        this.isConnected = true;
                        this.prepareHeartbeat();
                        logger.info(`WebSocket connection is now open`);
                        resolve(this.isConnected);
                    },
                },
                closeObserver: {
                    next: (closeEvent) => {
                        this.disconnectedByServer = !closeEvent.wasClean;
                        this.isConnected = false;
                        if (this.disconnectedByServer) {
                            logger.info(`WebSocket connection was closed due an error - Code: ${closeEvent.code} ${closeEvent.reason ? ' | Reason: ' + closeEvent.reason : ''}`);
                        }
                        resolve(this.isConnected);
                    },
                },
                serializer: (object) => this.serializer.serialize(object),
                deserializer: (event) => this.serializer.deserialize(event),
            });
            const onNextMessage = (receivedMessage) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const responseMessageReceivedLog = `Response Message received for SourceRef: ${receivedMessage.SOURCE_REF} | MessageType: ${receivedMessage.MESSAGE_TYPE}`;
                this.logMessageTypeFlow(receivedMessage.MESSAGE_TYPE, logger.info, responseMessageReceivedLog);
                const { SOURCE_REF, MESSAGE_TYPE } = receivedMessage;
                const messageHandler = this.getMessageHandler(SOURCE_REF, MESSAGE_TYPE);
                if (!messageHandler) {
                    return;
                }
                let nacked = false;
                switch (receivedMessage.MESSAGE_TYPE) {
                    case EventMessageType.EVENT_LOGIN_AUTH_ACK: {
                        this.hasValidSession = true;
                        const systemDetails = receivedMessage.DETAILS;
                        // Reconfigures the heartbeat with system details from the login response
                        if (systemDetails.HEARTBEAT_INTERVAL_SECS &&
                            ((_e = this.connectOptions) === null || _e === void 0 ? void 0 : _e.heartbeatInterval) === undefined) {
                            this.heartbeatInterval =
                                systemDetails.HEARTBEAT_INTERVAL_SECS * this.secondsMultiplier;
                            this.prepareHeartbeat();
                        }
                        break;
                    }
                    case MessageType.LOGOUT_ACK: {
                        this.hasValidSession = false;
                        messageHandler.onReceivedMessage.call(this, receivedMessage);
                        this.cleanMessages();
                        return;
                    }
                    case MessageType.LOGON_ACK:
                        return;
                    case MessageType.LOGOFF_ACK: {
                        if (receivedMessage.LOGGED_OUT && this.hasValidSession) {
                            const refreshTokenMessage = this.messageBuilder.createRefreshTokenMessage();
                            const refreshTokenResult = yield this.send(refreshTokenMessage);
                            if (!refreshTokenResult) {
                                this.hasValidSession = false;
                            }
                            else if (refreshTokenResult.MESSAGE_TYPE === EventMessageType.EVENT_LOGIN_AUTH_ACK) {
                                this.session.setAuthResult(refreshTokenResult);
                                this.wsMessageHandlers.forEach((mh) => {
                                    if (mh.isSubscription) {
                                        mh.sentMessage.SESSION_AUTH_TOKEN = refreshTokenResult.SESSION_AUTH_TOKEN;
                                        this.websocket.next(mh.sentMessage);
                                    }
                                });
                            }
                        }
                        break;
                    }
                    case MessageType.QUERY_UPDATE: {
                        if (!messageHandler.isSubscription) {
                            const dataLogoffMessage = this.messageBuilder.createDataLogoffMessage(SOURCE_REF);
                            this.send(dataLogoffMessage);
                        }
                        break;
                    }
                    case MessageType.MSG_NACK: {
                        nacked = true;
                        break;
                    }
                }
                if (nacked) {
                    messageHandler.onReceivedError.call(this, {
                        receivedMessage,
                        sentMessage: messageHandler.sentMessage,
                    });
                }
                else {
                    messageHandler.onReceivedMessage.call(this, receivedMessage);
                }
                this.wsMessages.next(receivedMessage);
                if (!messageHandler.isSubscription) {
                    this.removeMessageHandler(SOURCE_REF);
                }
            });
            const onError = (error) => {
                logger.debug('Error found: ', error);
                this.reconnect();
            };
            const onComplete = () => {
                var _a;
                if (this.disconnectedByServer && ((_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.alwaysOn)) {
                    this.reconnect();
                    return;
                }
                logger.info('Connection is now closed');
            };
            this.socketSubscription = this.websocket.subscribe((_d = (_c = this.connectOptions) === null || _c === void 0 ? void 0 : _c.connectObserver) !== null && _d !== void 0 ? _d : {
                next: onNextMessage,
                error: onError,
                complete: onComplete,
            });
        });
    }
    prepareHeartbeat() {
        var _a, _b, _c, _d;
        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
        }
        const sendHeartbeat = () => __awaiter(this, void 0, void 0, function* () {
            if (!this.isConnected)
                return;
            const ping = {
                MESSAGE_TYPE: MessageType.HEARTBEAT_PING,
                SOURCE_REF: this.uuid.createId(),
            };
            const pong = yield this.send(ping);
            this.heartbeatIsEnabled =
                pong.MESSAGE_TYPE === MessageType.HEARTBEAT_PONG && pong.SOURCE_REF === ping.SOURCE_REF;
        });
        if ((_b = (_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.enableHeartbeat) !== null && _b !== void 0 ? _b : this.heartbeatIsEnabled) {
            const intervalInMillisecocnds = (_d = (_c = this.connectOptions) === null || _c === void 0 ? void 0 : _c.heartbeatInterval) !== null && _d !== void 0 ? _d : this.heartbeatInterval;
            this.heartbeatSubscription = timer(intervalInMillisecocnds, intervalInMillisecocnds)
                .pipe(takeWhile(() => this.websocket && this.isConnected), tap(() => this.heartbeatLoggingIsEnabled &&
                logger.debug(`Sending a heartbeat ping every ${intervalInMillisecocnds} ms`)))
                .subscribe(() => sendHeartbeat());
        }
    }
    reconnect() {
        var _a, _b, _c, _d, _e;
        const notAlwaysOnAndHeartbeating = !((_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.alwaysOn) && ((_b = this.connectOptions) === null || _b === void 0 ? void 0 : _b.enableHeartbeat) === false;
        if (this.isReconnecting || notAlwaysOnAndHeartbeating) {
            return;
        }
        this.isReconnecting = true;
        const scheduler = ((_c = this.reconnectOptions) === null || _c === void 0 ? void 0 : _c.reconnectStrategy) === SocketReconnectStrategy.Exponential
            ? exponentialScheduler
            : linearScheduler;
        const reconnectObservable = this.isConnectedSubject.asObservable().pipe(switchMap((isConnected) => !isConnected
            ? retryInterval(this.reconnectOptions, scheduler).pipe(mergeMap((value) => {
                var _a, _b, _c;
                let attemptsAmount;
                if ((_a = this.connectOptions) === null || _a === void 0 ? void 0 : _a.alwaysOn) {
                    attemptsAmount = value + 1;
                }
                else {
                    attemptsAmount =
                        (_c = (_b = this.reconnectOptions) === null || _b === void 0 ? void 0 : _b.reconnectAttempts) !== null && _c !== void 0 ? _c : MAX_RECONNECT_ATTEMPTS;
                }
                if (value < attemptsAmount) {
                    return of(value);
                }
                return throwError(() => new Error(`Max reconnect attempts ${attemptsAmount} reached.`));
            }))
            : of(-1)), takeWhile(() => this.websocket && !this.isConnected));
        reconnectObservable.subscribe((_e = (_d = this.reconnectOptions) === null || _d === void 0 ? void 0 : _d.reconnectObserver) !== null && _e !== void 0 ? _e : {
            next: (value) => {
                logger.debug(`Attempt no. ${value + 1} to reconnect`);
                this.websocket.complete();
                this.connect(this.url, this.connectOptions, this.reconnectOptions);
            },
            error: (error) => {
                var _a;
                logger.debug(error);
                (_a = this.socketSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
                this.isReconnecting = false;
            },
            complete: () => {
                logger.info(this.isConnected ? 'Reconnected' : 'Disconnected');
                this.isReconnecting = false;
                this.wsMessageHandlers.forEach((mh) => {
                    if (mh.isSubscription) {
                        this.websocket.next(mh.sentMessage);
                    }
                });
            },
        });
    }
    reset() {
        this.isConnected = false;
        this.websocket && this.websocket.complete();
        this.websocket = null;
        this.cleanMessages();
    }
    cleanMessages() {
        this.wsMessages && this.wsMessages.complete();
        this.wsMessages = new SocketSubject();
        this.wsMessageHandlers = new Map();
    }
    send(message, needsHandling = true) {
        if (!this.isConnected) {
            Promise.reject(new Error('WebSocket connection is closed'));
            return;
        }
        const sendingMessasgeLog = `Sending Message for SourceRef: ${message.SOURCE_REF} | MessageType: ${message.MESSAGE_TYPE}`;
        this.logMessageTypeFlow(message.MESSAGE_TYPE, logger.info, sendingMessasgeLog);
        return new Promise((resolve, reject) => {
            const onMessage = (msg) => {
                resolve(msg);
            };
            const onError = (err) => {
                reject(err);
            };
            if (needsHandling) {
                this.setMessageHandler(message, onMessage, onError);
            }
            this.websocket.next(message);
        });
    }
    sendForStream(message, onMessage, onError) {
        if (!this.isConnected) {
            return throwError(() => new Error('WebSocket connection is closed'));
        }
        if (!this.hasValidSession) {
            return throwError(() => new Error(`WebSocket connection is open but there is no valid/active session, please check your auth flow`));
        }
        const sendingMessageLog = `Sending Message (stream) for SourceRef: ${message.SOURCE_REF} | MessageType: ${message.MESSAGE_TYPE}`;
        this.logMessageTypeFlow(message.MESSAGE_TYPE, logger.info, sendingMessageLog);
        this.setMessageHandler(message, onMessage, onError, true);
        this.websocket.next(message);
        return from(this.wsMessages.pipe(filter((msg) => msg.SOURCE_REF === message.SOURCE_REF)));
    }
    setMessageHandler(message, onMessage, onError, isSubscription = false) {
        const settingMessageHandlerLog = `Setting Message Handler for SourceRef: ${message.SOURCE_REF} | MessageType: ${message.MESSAGE_TYPE}`;
        this.logMessageTypeFlow(message.MESSAGE_TYPE, logger.debug, settingMessageHandlerLog);
        this.wsMessageHandlers.set(message.SOURCE_REF, new SocketMessageHandler(message, onMessage, onError, isSubscription));
    }
    removeMessageHandler(sourceRef) {
        this.wsMessageHandlers.delete(sourceRef);
    }
    getMessageHandler(sourceRef, messageType) {
        const gettingMessageHandlerLog = `Getting Message Handler for SourceRef: ${sourceRef} | MessageType: ${messageType}`;
        this.logMessageTypeFlow(messageType, logger.debug, gettingMessageHandlerLog);
        return this.wsMessageHandlers.get(sourceRef);
    }
    logMessageTypeFlow(messageType, logFunc, logMsg) {
        switch (messageType) {
            case MessageType.HEARTBEAT_PING:
            case MessageType.HEARTBEAT_PONG:
                if (this.heartbeatLoggingIsEnabled) {
                    logFunc(logMsg);
                }
                break;
            default:
                if (this.messageLoggingIsEnabled) {
                    logFunc(logMsg);
                }
                break;
        }
    }
};
__decorate([
    observable
], SocketService.prototype, "websocket", void 0);
__decorate([
    observable
], SocketService.prototype, "wsMessages", void 0);
__decorate([
    observable
], SocketService.prototype, "wsMessageHandlers", void 0);
__decorate([
    observable
], SocketService.prototype, "isConnected", void 0);
__decorate([
    observable
], SocketService.prototype, "disconnectedByServer", void 0);
__decorate([
    observable
], SocketService.prototype, "hasValidSession", void 0);
__decorate([
    observable
], SocketService.prototype, "heartbeatIsEnabled", void 0);
__decorate([
    observable
], SocketService.prototype, "isReconnecting", void 0);
SocketService = __decorate([
    __param(0, MessageBuilder),
    __param(1, Session),
    __param(2, JSONSerializer),
    __param(3, UUID)
], SocketService);
/**
 * The DI token for the Socket interface.
 * @internal
 */
export const Socket = DI.createInterface((x) => x.singleton(SocketService));
